// The /account profile photo: pick or drop a photo, choose its square (drag,
// pinch, wheel, slider or keys), upload with progress, or remove it. Hidden
// controls while the server has no media storage.
import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent, type PointerEvent } from "react";
import { api, ApiError, errorMessage, session, unwrap } from "../api";
import { avatarErrorText, initialCrop, maxZoom, panCrop, zoomCrop, type Crop } from "../account/avatar";
import { drawPreview, encodeCrop, loadPhoto, PhotoError, uploadAvatar, type Photo } from "../account/avatarCanvas";
import { MemberAvatar } from "./MemberAvatar";

type User = Schemas["User"];

/** Arrow keys move the photo this many CSS pixels; +/- zoom by this factor. */
const KEY_PAN_PX = 10;
const KEY_ZOOM = 1.15;

function photoErrorText(error: unknown): string {
  if (error instanceof PhotoError) return error.message;
  if (error instanceof ApiError) return avatarErrorText(error.status, error.code) ?? errorMessage(error);
  return errorMessage(error);
}

export function ProfilePhotoCard({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const capabilities = useQuery({
    queryKey: ["capabilities"],
    queryFn: async () => unwrap(await api.GET("/capabilities")),
    staleTime: Infinity,
  });
  // Unknown until capabilities load; without media there's nothing to change.
  const enabled = capabilities.data ? capabilities.data.media !== undefined : null;
  const [photo, setPhoto] = useState<Photo | null>(null);
  // A new photo starts a fresh crop.
  const [editor, setEditor] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [dropping, setDropping] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const choose = useRef<HTMLButtonElement>(null);
  // Closing the editor puts focus back on Choose/Change photo.
  const refocus = useRef(false);
  const upload = useRef<AbortController | null>(null);
  // Picks that finish decoding after a newer one (or after leaving) are dropped.
  const pick = useRef(0);

  useEffect(
    () => () => {
      pick.current++;
      upload.current?.abort();
    },
    [],
  );
  useEffect(() => () => photo?.bitmap.close(), [photo]);

  async function changed(user: User) {
    queryClient.setQueryData(["me"], user);
    // Circle pages list members with their photos; live maps and chats get frames.
    await queryClient.invalidateQueries({ queryKey: ["circle"] });
  }

  function failed(cause: unknown) {
    setError(photoErrorText(cause));
    if (cause instanceof ApiError && cause.code === "media_disabled") {
      pick.current++;
      setPhoto(null);
      void queryClient.invalidateQueries({ queryKey: ["capabilities"] });
    }
  }

  const remove = useMutation({
    mutationFn: async () => {
      const result = await api.DELETE("/me/avatar");
      if (!result.response.ok) unwrap({ ...result, data: undefined });
    },
    onMutate: () => setError(null),
    onSuccess: () => changed({ ...user, avatar_url: null, avatar_small_url: null }),
    onError: failed,
  });
  const busy = progress !== null || remove.isPending;
  // After the button is enabled again: a disabled one can't take focus.
  useEffect(() => {
    if (photo || busy || !refocus.current) return;
    refocus.current = false;
    choose.current?.focus();
  }, [photo, busy]);

  async function open(file: File | undefined) {
    if (!file || progress !== null || enabled !== true) return;
    const id = ++pick.current;
    setError(null);
    try {
      const loaded = await loadPhoto(file);
      if (id !== pick.current) {
        loaded.bitmap.close();
        return;
      }
      setPhoto(loaded);
      setEditor(id);
    } catch (cause) {
      if (id === pick.current) failed(cause);
    }
  }

  async function save(crop: Crop) {
    if (!photo) return;
    // A photo still decoding from "Choose another" never replaces the one being saved.
    pick.current++;
    const controller = new AbortController();
    upload.current = controller;
    setError(null);
    setProgress(0);
    try {
      const blob = await encodeCrop(photo, crop);
      const updated = await uploadAvatar(session, blob, setProgress, controller.signal);
      if (controller.signal.aborted) return;
      refocus.current = true;
      setPhoto(null);
      await changed(updated);
    } catch (cause) {
      if (!controller.signal.aborted) failed(cause);
    } finally {
      if (upload.current === controller) {
        upload.current = null;
        setProgress(null);
      }
    }
  }

  function cancel() {
    pick.current++;
    if (upload.current) {
      upload.current.abort();
      // Cancel is offered only until the body is sent, but the server may have taken it just then.
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    }
    upload.current = null;
    refocus.current = true;
    setProgress(null);
    setPhoto(null);
    setError(null);
  }

  const withFiles = (event: DragEvent) => enabled === true && !busy && event.dataTransfer.types.includes("Files");

  return (
    <section
      className={`card stack profile-photo-card${dropping ? " dropping" : ""}`}
      onDragOver={(event) => {
        if (!withFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setDropping(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(false);
      }}
      onDrop={(event) => {
        setDropping(false);
        if (!withFiles(event)) return;
        event.preventDefault();
        void open(event.dataTransfer.files[0]);
      }}
    >
      <h2>Profile photo</h2>
      {photo && enabled !== false ? (
        <CropEditor
          key={editor}
          photo={photo}
          progress={progress}
          onSave={(crop) => void save(crop)}
          onCancel={cancel}
          onPickAnother={() => input.current?.click()}
        />
      ) : (
        <div className="profile-photo-row">
          <span className="profile-photo">
            <MemberAvatar userId={user.id} name={user.name} avatarUrl={user.avatar_url ?? null} isSelf={false} />
          </span>
          <div className="stack">
            {enabled === false ? (
              <p className="muted">Profile photos aren't available right now. Your circles see your initials.</p>
            ) : (
              <p className="muted">
                {user.avatar_url
                  ? "Shown to the people in your circles and chats instead of your initials."
                  : "Add a photo for the people in your circles and chats. Until then they see your initials."}
              </p>
            )}
            {enabled && (
              <div className="button-row">
                <button ref={choose} type="button" disabled={busy} onClick={() => input.current?.click()}>
                  {user.avatar_url ? "Change photo" : "Choose photo"}
                </button>
                {user.avatar_url && (
                  <button
                    type="button"
                    className="secondary"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm("Remove your profile photo? People will see your initials again."))
                        remove.mutate();
                    }}
                  >
                    {remove.isPending ? "Removing…" : "Remove"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {dropping && (
        <div className="profile-photo-drop" aria-hidden="true">
          Drop a photo
        </div>
      )}
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          void open(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
    </section>
  );
}

/** The square crop over the photo, with a round mask showing how markers draw it. */
function CropEditor(props: {
  photo: Photo;
  progress: number | null;
  onSave: (crop: Crop) => void;
  onCancel: () => void;
  onPickAnother: () => void;
}) {
  const { photo, progress } = props;
  const size = photo.size;
  const [crop, setCrop] = useState<Crop>(() => initialCrop(size));
  const canvas = useRef<HTMLCanvasElement>(null);
  // Pointers down on the canvas, for dragging and pinching.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const uploading = progress !== null;
  // Once the whole body is sent the server is saving it: too late to cancel.
  const sent = progress !== null && progress >= 1;
  const highest = maxZoom(size);

  useEffect(() => canvas.current?.focus(), []);

  useEffect(() => {
    if (canvas.current) drawPreview(canvas.current, photo, crop);
  }, [photo, crop]);

  // React's wheel listener is passive, so it can't stop the page from scrolling.
  useEffect(() => {
    const element = canvas.current;
    if (!element || uploading) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const box = element.getBoundingClientRect();
      const anchor = { x: (event.clientX - box.left) / box.width, y: (event.clientY - box.top) / box.height };
      setCrop((c) => zoomCrop(size, c, c.zoom * Math.exp(-event.deltaY * 0.002), anchor));
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, [size, uploading]);

  function down(event: PointerEvent<HTMLCanvasElement>) {
    if (uploading) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
  }

  function move(event: PointerEvent<HTMLCanvasElement>) {
    const previous = pointers.current.get(event.pointerId);
    if (!previous || uploading) return;
    const box = event.currentTarget.getBoundingClientRect();
    const next = { x: event.clientX, y: event.clientY };
    const other = [...pointers.current.entries()].find(([id]) => id !== event.pointerId)?.[1];
    pointers.current.set(event.pointerId, next);
    if (!other) {
      setCrop((c) => panCrop(size, c, next.x - previous.x, next.y - previous.y, box.width));
      return;
    }
    // Pinch: zoom by the change in spread about the fingers' midpoint, then follow the midpoint.
    const before = Math.hypot(previous.x - other.x, previous.y - other.y);
    const after = Math.hypot(next.x - other.x, next.y - other.y);
    if (before <= 0) return;
    const mid = { x: (next.x + other.x) / 2, y: (next.y + other.y) / 2 };
    const anchor = { x: (mid.x - box.left) / box.width, y: (mid.y - box.top) / box.height };
    setCrop((c) =>
      panCrop(
        size,
        zoomCrop(size, c, (c.zoom * after) / before, anchor),
        (next.x - previous.x) / 2,
        (next.y - previous.y) / 2,
        box.width,
      ),
    );
  }

  function up(event: PointerEvent<HTMLCanvasElement>) {
    pointers.current.delete(event.pointerId);
  }

  function key(event: KeyboardEvent<HTMLCanvasElement>) {
    if (uploading) return;
    const width = event.currentTarget.clientWidth || 1;
    const pans: Record<string, [number, number]> = {
      ArrowLeft: [KEY_PAN_PX, 0],
      ArrowRight: [-KEY_PAN_PX, 0],
      ArrowUp: [0, KEY_PAN_PX],
      ArrowDown: [0, -KEY_PAN_PX],
    };
    const pan = pans[event.key];
    if (pan) setCrop((c) => panCrop(size, c, pan[0], pan[1], width));
    else if (event.key === "+" || event.key === "=") setCrop((c) => zoomCrop(size, c, c.zoom * KEY_ZOOM));
    else if (event.key === "-") setCrop((c) => zoomCrop(size, c, c.zoom / KEY_ZOOM));
    else return;
    event.preventDefault();
  }

  return (
    <div className="stack">
      <div className="avatar-crop">
        <canvas
          ref={canvas}
          tabIndex={0}
          role="img"
          aria-label="Your photo's crop. Drag or use the arrow keys to move it; scroll, pinch or use + and - to zoom."
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onLostPointerCapture={up}
          onKeyDown={key}
        />
        <span className="avatar-crop-mask" aria-hidden="true" />
      </div>
      <label className="avatar-zoom">
        Zoom
        <input
          type="range"
          min={1}
          max={highest}
          step={0.01}
          value={crop.zoom}
          disabled={uploading || highest <= 1}
          onChange={(event) => setCrop((c) => zoomCrop(size, c, Number(event.target.value)))}
        />
      </label>
      {uploading && (
        <p className="muted" role="status">
          {sent ? "Saving…" : "Uploading…"} <progress max={1} value={progress} aria-label="Upload progress" />
        </p>
      )}
      <div className="button-row">
        <button type="button" disabled={uploading} onClick={() => props.onSave(crop)}>
          Save photo
        </button>
        <button type="button" className="secondary" disabled={uploading} onClick={props.onPickAnother}>
          Choose another
        </button>
        <button type="button" className="secondary" disabled={sent} onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
